import React, { useState, useEffect } from 'react';
import YourFabricWizard from '../components/YourFabricWizard';
import { 
  ArrowLeftIcon,
  CheckCircleIcon,
  PlayIcon,
  DocumentTextIcon,
  ChartBarIcon,
  CogIcon,
  PlusIcon,
  TrashIcon,
  PencilIcon,
  RocketLaunchIcon
} from '@heroicons/react/24/outline';

export default function YourFabric() {
  const [currentView, setCurrentView] = useState('configurations'); // 'wizard', 'configurations', 'testing'
  const [configurations, setConfigurations] = useState([]);
  const [selectedConfig, setSelectedConfig] = useState(null);
  const [testDocument, setTestDocument] = useState('');
  const [testResults, setTestResults] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadConfigurations();
  }, []);

  const loadConfigurations = async () => {
    try {
      const response = await fetch('/api/v1/yourfabric/configs');
      if (response.ok) {
        const data = await response.json();
        setConfigurations(data.configs || []);
      }
    } catch (err) {
      console.error('Failed to load configurations:', err);
    }
  };

  const handleWizardComplete = (result) => {
    setCurrentView('configurations');
    loadConfigurations(); // Refresh the list
  };

  const handleDeleteConfiguration = async (configId) => {
    if (window.confirm('Are you sure you want to delete this configuration?')) {
      try {
        const response = await fetch(`/api/v1/yourfabric/configs/${configId}`, {
          method: 'DELETE'
        });
        if (response.ok) {
          loadConfigurations();
        }
      } catch (err) {
        console.error('Failed to delete configuration:', err);
      }
    }
  };

  const handleLaunchInstance = (configId) => {
    // Launch the YourFabric instance with the specific configuration
    const instanceUrl = `http://localhost:3005?config_id=${configId}`;
    window.open(instanceUrl, '_blank');
  };

  const handleTestConfiguration = async () => {
    if (!selectedConfig || !testDocument.trim()) return;
    
    setLoading(true);
    try {
      const response = await fetch(`/api/v1/yourfabric/configs/${selectedConfig.config_id}/test`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sample_documents: [testDocument] })
      });
      
      if (response.ok) {
        const data = await response.json();
        
        // Parse the test results from the API response
        if (data.test_results && data.test_results.test_results && data.test_results.test_results.length > 0) {
          const result = data.test_results.test_results[0];
          const classificationText = result.classification_result;
          
          // Extract categories and confidence scores from the text
          const categoryMatches = classificationText.match(/Category:\s*([^,]+),\s*Confidence:\s*([\d.]+)/g) || [];
          const classifications = categoryMatches.map(match => {
            const parts = match.match(/Category:\s*([^,]+),\s*Confidence:\s*([\d.]+)/);
            return {
              category: parts[1].trim(),
              confidence: parseFloat(parts[2])
            };
          });
          
          // Set the first as primary, rest as secondary
          const formattedResults = {
            primary_classification: classifications[0]?.category || 'Unknown',
            confidence: classifications[0]?.confidence || 0,
            secondary_classifications: classifications.slice(1),
            raw_result: classificationText
          };
          
          setTestResults(formattedResults);
        } else {
          setTestResults(data);
        }
      }
    } catch (err) {
      console.error('Failed to test configuration:', err);
    } finally {
      setLoading(false);
    }
  };

  // Main Configurations View
  if (currentView === 'configurations') {
    return (
      <div className="max-w-6xl mx-auto">
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">YourFabric</h1>
              <p className="text-lg text-gray-600 mt-2">
                AI-powered custom classification systems tailored to your needs
              </p>
            </div>
            <button
              onClick={() => setCurrentView('wizard')}
              className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
            >
              <PlusIcon className="w-4 h-4 mr-2" />
              Create Configuration
            </button>
          </div>
        </div>

        {/* Configurations Grid */}
        <div className="space-y-6">
          {configurations.length === 0 ? (
            <div className="text-center py-12">
              <CogIcon className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No configurations yet</h3>
              <p className="text-gray-500 mb-6">Create your first AI classification system to get started.</p>
              <button
                onClick={() => setCurrentView('wizard')}
                className="flex items-center mx-auto px-6 py-3 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
              >
                <PlusIcon className="w-5 h-5 mr-2" />
                Create Configuration
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {configurations.map((config) => (
                <div key={config.config_id} className="bg-white rounded-lg border border-gray-200 shadow-sm hover:shadow-md transition-shadow">
                  <div className="p-6">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex-1">
                        <h3 className="text-lg font-medium text-gray-900 mb-2">{config.config_name}</h3>
                        <p className="text-sm text-gray-600 mb-3">{config.description}</p>
                        <div className="flex items-center text-xs text-gray-500">
                          <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded-full">{config.domain}</span>
                          <span className="ml-2">{config.classification_schema?.length || 0} categories</span>
                        </div>
                      </div>
                      <div className="flex space-x-1">
                        <button
                          onClick={() => handleLaunchInstance(config.config_id)}
                          className="p-2 text-green-600 hover:bg-green-50 rounded-md"
                          title="Launch Instance"
                        >
                          <RocketLaunchIcon className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => {
                            setSelectedConfig(config);
                            setCurrentView('testing');
                          }}
                          className="p-2 text-blue-600 hover:bg-blue-50 rounded-md"
                          title="Test Configuration"
                        >
                          <PlayIcon className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteConfiguration(config.config_id)}
                          className="p-2 text-red-600 hover:bg-red-50 rounded-md"
                          title="Delete Configuration"
                        >
                          <TrashIcon className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                    
                    <div className="border-t pt-4">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-gray-500">Created: {new Date(config.created_at).toLocaleDateString()}</span>
                        <span className="text-green-600 font-medium">Ready</span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  // Testing View
  if (currentView === 'testing' && selectedConfig) {
    return (
      <div className="max-w-4xl mx-auto">
        <div className="mb-6 flex items-center">
          <button
            onClick={() => setCurrentView('configurations')}
            className="flex items-center px-4 py-2 text-sm text-gray-600 hover:text-gray-900 transition-colors mr-4"
          >
            <ArrowLeftIcon className="w-4 h-4 mr-2" />
            Back to Configurations
          </button>
          
          <div className="flex-1">
            <h1 className="text-2xl font-bold text-gray-900">{selectedConfig.config_name}</h1>
            <p className="text-sm text-gray-600">{selectedConfig.description}</p>
          </div>
        </div>

        <div className="space-y-6">
          {/* Test Input */}
          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Test Your Configuration</h3>
            <textarea
              value={testDocument}
              onChange={(e) => setTestDocument(e.target.value)}
              placeholder="Enter text you want to classify..."
              className="w-full h-40 p-4 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
            />
            
            <div className="mt-4 flex justify-end">
              <button
                onClick={handleTestConfiguration}
                disabled={!testDocument.trim() || loading}
                className="flex items-center px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
              >
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Classifying...
                  </>
                ) : (
                  <>
                    <PlayIcon className="w-4 h-4 mr-2" />
                    Classify Text
                  </>
                )}
              </button>
            </div>
          </div>

          {/* Test Results */}
          {testResults && (
            <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Classification Results</h3>
              
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm font-medium text-gray-500 mb-2">Primary Classification</p>
                    <div className="p-3 bg-blue-50 rounded-md">
                      <p className="font-medium text-blue-900">{testResults.primary_classification}</p>
                      <p className="text-sm text-blue-700">
                        Confidence: {Math.round(testResults.confidence * 100)}%
                      </p>
                    </div>
                  </div>
                  
                  {testResults.secondary_classifications && testResults.secondary_classifications.length > 0 && (
                    <div>
                      <p className="text-sm font-medium text-gray-500 mb-2">Secondary Classifications</p>
                      <div className="space-y-2">
                        {testResults.secondary_classifications.map((classification, index) => (
                          <div key={index} className="p-2 bg-gray-50 rounded-md">
                            <p className="text-sm font-medium text-gray-900">{classification.category}</p>
                            <p className="text-xs text-gray-600">
                              Confidence: {Math.round(classification.confidence * 100)}%
                            </p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
                
                {(testResults.explanation || testResults.raw_result) && (
                  <div>
                    <p className="text-sm font-medium text-gray-500 mb-2">AI Reasoning</p>
                    <div className="p-4 bg-gray-50 rounded-md">
                      <p className="text-sm text-gray-700 whitespace-pre-wrap">{testResults.explanation || testResults.raw_result}</p>
                    </div>
                  </div>
                )}
                
                {testResults.extracted_entities && testResults.extracted_entities.length > 0 && (
                  <div>
                    <p className="text-sm font-medium text-gray-500 mb-2">Key Entities</p>
                    <div className="flex flex-wrap gap-2">
                      {testResults.extracted_entities.map((entity, index) => (
                        <span key={index} className="px-2 py-1 text-xs bg-green-100 text-green-800 rounded-full">
                          {entity}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Configuration Details */}
          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Configuration Details</h3>
            
            <div className="space-y-4">
              <div>
                <p className="text-sm font-medium text-gray-500 mb-2">Classification Categories</p>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                  {selectedConfig.classification_schema?.map((category, index) => (
                    <span key={index} className="px-3 py-1 text-sm bg-blue-100 text-blue-800 rounded-full">
                      {category.name}
                    </span>
                  ))}
                </div>
              </div>
              
              {selectedConfig.semantic_bridges && selectedConfig.semantic_bridges.length > 0 && (
                <div>
                  <p className="text-sm font-medium text-gray-500 mb-2">AI Reasoning Bridges</p>
                  <div className="space-y-2">
                    {selectedConfig.semantic_bridges.slice(0, 3).map((bridge, index) => (
                      <div key={index} className="p-2 bg-gray-50 rounded-md">
                        <p className="text-xs text-gray-700">{bridge}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Wizard View
  return (
    <div>
      <YourFabricWizard onComplete={handleWizardComplete} />
    </div>
  );
}